import React, { useEffect, useState } from 'react'
import classNames from 'classnames'
import { useAlert } from 'react-alert'
import styles from '../../styles.module.scss'
import AddModal from './AddModal/addModal'
import { database, databaseRef } from 'utils/firebase'
import { onValue, update } from 'firebase/database'
import Template from '../templateComponent/Template'
import { Col, Row } from 'react-bootstrap'
import ContentLoader from 'react-content-loader'
import { useSelector } from 'react-redux'
import { addDoctorLog } from 'utils/functions'

const MedicationsTemplates = () => {
  const [addTemplateOpen, setAddTemplateOpen] = useState(false)
  const [editTemplateOpen, setEditTemplateOpen] = useState(false)
  const [templates, setTemplates] = useState(null)
  const [fetched, setFetched] = useState(false)
  const [editKey, setEditKey] = useState(null)
  const alert = useAlert()
  const [searchString, setSearchString] = useState('')
  const { me } = useSelector((state) => state.app)

  const toggle = (ref) => {
    ref((v) => !v)
  }

  useEffect(() => {
    // Define the database reference
    const templatesRef = databaseRef(database, 'templates/medications')

    // Attach the event listener using onValue
    const unsubscribe = onValue(templatesRef, (snapshot) => {
      setTemplates(snapshot.val() || null)
      setFetched(true)
    })

    // Cleanup function to detach the event listener when the component unmounts
    return () => unsubscribe()
  }, [])

  const editTemplate = (key) => {
    setEditKey(key)
    toggle(setEditTemplateOpen)
  }
  const deleteTemplate = (key) => {
    const confirmed = window.confirm(
      'Are you sure you want to delete this template?',
    )

    if (confirmed) {
      const templateName = templates[key]?.name || 'Unknown Template'
      const updates = {}
      updates[`templates/medications/${key}`] = null
      update(databaseRef(database), updates)
        .then(() => {
          alert.success('Deleted')
          addDoctorLog({
            action: 'DELETE_MEDICATION_TEMPLATE',
            category: 'template',
            description: `Deleted medication template: ${templateName}`,
            doctorID: me.id,
            details: {
              templateId: key,
              templateName: templateName,
              timestamp: Date.now()
            }
          })
        })
        .catch(error => {
          console.error('Error deleting template:', error)
          alert.error('Failed to delete template')
        })
    }
  }

  return (
    <div className={styles.bodyRoot}>
      {/* -- Search and Add -- */}
      <div className="d-flex justify-content-between align-items-center">
        <label
          htmlFor="name"
          className={classNames('d-flex flex-column w-50', styles.title)}
        >
          Search template :
          <input
            type="text"
            name="name"
            id="name"
            onChange={(e) => {
              setSearchString(e.target.value)
            }}
          />
        </label>
        <button
          type="button"
          className={styles.button}
          onClick={() => toggle(setAddTemplateOpen)}
        >
          Add template
        </button>
      </div>
      {fetched &&
        templates &&
        Object.keys(templates)
          .filter((key) =>
            templates[key].name
              .toLowerCase()
              .includes(searchString.toLowerCase()),
          )
          .map((key) => {
            const template = templates[key]
            const medication = template.medicine
            return (
              <Template
                key={key}
                title={template.name}
                editOnClick={() => editTemplate(key)}
                deleteOnClick={() => deleteTemplate(key)}
              >
                <div className="p-2">
                  <Row className="mt-2">
                    <Col className="d-flex justify-content-between ">
                      <span className={styles.label}>Name :</span>
                      <span className={styles.value}>
                        {medication?.name || '-'}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-between">
                      <span className={styles.label}>Composition:</span>
                      <span className={styles.value}>
                        {medication?.composition || '-'}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-between ">
                      <span className={styles.label}>Route:</span>
                      <span className={styles.value}>
                        {medication?.route || '-'}
                      </span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col className="d-flex justify-content-between ">
                      <span className={styles.label}>Dosage:</span>
                      <span className={styles.value}>
                        {medication?.dosage || '-'}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-between ">
                      <span className={styles.label}>Frequency:</span>
                      <span className={styles.value}>
                        {medication?.frequency || '-'}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-between ">
                      <span className={styles.label}>Duration:</span>
                      <span className={styles.value}>
                        {medication?.duration || '-'}
                      </span>
                    </Col>
                  </Row>
                  <Row className="mt-2">
                    <Col className="d-flex justify-content-between align-items-start">
                      <span className={styles.label}>Quantity:</span>
                      <span className={styles.value}>
                        {medication?.quantity || '-'}
                      </span>
                    </Col>
                    <Col className="d-flex justify-content-between align-items-start">
                      <span className={styles.label}>Refills:</span>
                      <span className={styles.value}>
                        {medication?.refills || '-'}
                      </span>
                    </Col>
                    <Col />
                  </Row>
                  <Row className="mb-4">
                    <Col className="d-flex mt-2">
                      <span className={styles.label}>Instructions:</span>
                      <span
                        className={classNames(styles.value, 'w-100', 'ml-4')}
                      >
                        {medication?.instructions || '-'}
                      </span>
                    </Col>
                  </Row>
                  <Row className="mb-4">
                    <Col className="d-flex mt-2">
                      <span className={styles.label}>Warnings:</span>
                      <span
                        className={classNames(styles.value, 'w-100', 'ml-4')}
                      >
                        {medication?.warning || '-'}
                      </span>
                    </Col>
                  </Row>
                </div>
              </Template>
            )
          })}

      {fetched && !templates && (
        <div className="d-flex justify-content-center align-items-center h-100">
          <span className={styles.title}>No templates found</span>
        </div>
      )}

      {/* -- Skeleton Loader -- */}
      {!fetched && !templates && (
        <div className="d-flex justify-content-center align-items-center h-100">
          <ContentLoader
            speed={2}
            width="100%"
            backgroundColor="#f3f3f3"
            foregroundColor="#bdbcbc"
          >
            <rect rx="4" ry="4" width="100%" height="20" />
            <rect y="30" rx="4" ry="4" width="100%" height="20" />
            <rect y="60" rx="4" ry="4" width="100%" height="20" />
            <rect y="90" rx="4" ry="4" width="100%" height="20" />
            <rect y="120" rx="4" ry="4" width="100%" height="20" />
          </ContentLoader>
        </div>
      )}

      {/*  -- Modals -- */}
      {addTemplateOpen && (
        <AddModal
          isOpen={addTemplateOpen}
          closeModal={() => toggle(setAddTemplateOpen)}
        />
      )}
      {editTemplateOpen && (
        <AddModal
          isOpen={editTemplateOpen}
          closeModal={() => toggle(setEditTemplateOpen)}
          editKey={editKey}
          edit
        />
      )}
    </div>
  )
}

export default MedicationsTemplates
